import { Head, Link, router, useForm } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"

import { HpiDateFilterBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, todayStr } from "@/lib/date-utils"
import { formatPhone } from "@/lib/format-phone"
import {
  editEmployeePath,
  employeeLogsPath,
  employeePath,
  employeesPath,
  reportsEmployeeLogsPath,
  resetPasswordEmployeePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface EmployeeLog {
  id: number
  log_date: string | null
  content: string
  created_by: string | null
  hidden: boolean
  created_at: string
}

interface Employee {
  id: number
  name: string
  first_name: string | null
  last_name: string | null
  initials: string | null
  formal_name: string | null
  report_display_name: string | null
  job_title: string | null
  email: string
  username: string | null
  work_phone: string | null
  cell_phone: string | null
  home_phone: string | null
  status: string
  employment_type: number | null
  worker_type: number | null
  org_level: number | null
  pay_rate: number | null
  pay_type: number | null
  mileage_rate: number | null
  pto_days: number | null
  rdo_days: number | null
  sick_days: number | null
  specialty: string | null
  quickbooks_name: string | null
  quickbooks_vendor: string | null
  permissions: Record<string, boolean>
  roles: string[]
  created_at: string
  updated_at: string
}

interface PendingItem {
  id: number
  type: string
  date: string | null
  case_id?: number
  customer_id?: number
  case_name: string | null
  description: string
}

type PermissionCategories = Record<string, string[]>

interface Props {
  employee: Employee
  logs: EmployeeLog[]
  log_start_date: string
  log_end_date: string
  permission_categories: PermissionCategories
  pending_items: PendingItem[]
  is_admin: boolean
  can_reset_password: boolean
}

export default function Show({
  employee,
  logs,
  log_start_date,
  log_end_date,
  permission_categories,
  pending_items,
  is_admin,
  can_reset_password,
}: Props) {
  const [showAddLog, setShowAddLog] = useState(false)
  const [logStartDate, setLogStartDate] = useState(log_start_date)
  const [logEndDate, setLogEndDate] = useState(log_end_date)
  const logForm = useForm({
    log_date: todayStr(),
    content: "",
    hidden: false,
  })

  logForm.transform((data) => ({ log: data }))

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Employees", href: employeesPath() },
    { title: employee.name, href: employeePath(employee.id) },
  ]

  const handleResetPassword = () => {
    if (
      confirm(
        `Are you sure you want to send a password reset email to ${employee.name}?`,
      )
    ) {
      router.patch(resetPasswordEmployeePath(employee.id))
    }
  }

  const handleLogFilter = () => {
    router.get(
      employeePath(employee.id),
      { log_start_date: logStartDate, log_end_date: logEndDate },
      { preserveState: true },
    )
  }

  const handleAddLog = (e: React.FormEvent) => {
    e.preventDefault()
    logForm.post(employeeLogsPath(employee.id), {
      onSuccess: () => {
        setShowAddLog(false)
        logForm.reset()
      },
    })
  }

  const handleDeleteLog = (logId: number) => {
    if (
      confirm(
        "Are you sure you want to delete this log entry? This cannot be undone.",
      )
    ) {
      router.delete(`/employees/${employee.id}/logs/${logId}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={employee.name} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{employee.name}</h1>
          <div className="flex gap-2">
            <Link
              href={editEmployeePath(employee.id)}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {can_reset_password && (
              <button
                onClick={handleResetPassword}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Send Password Reset Email
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Employment Info */}
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Employment Info</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <dt className="text-hpi-med-blue font-medium">Job Title</dt>
              <dd>{employee.job_title ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Email</dt>
              <dd>
                <a
                  href={`mailto:${employee.email}`}
                  className="text-hpi-active-blue hover:text-hpi-active-blue"
                >
                  {employee.email}
                </a>
              </dd>
              <dt className="text-hpi-med-blue font-medium">Username</dt>
              <dd>{employee.username ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Status</dt>
              <dd className="capitalize">{employee.status}</dd>
              <dt className="text-hpi-med-blue font-medium">Access Level</dt>
              <dd>
                {employee.org_level === 8
                  ? "Limited Access"
                  : employee.org_level && employee.org_level > 0
                    ? "Full Access"
                    : "None"}
              </dd>
              <dt className="text-hpi-med-blue font-medium">Roles</dt>
              <dd>{employee.roles.join(", ") || "-"}</dd>
              {employee.work_phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Work Phone</dt>
                  <dd>{formatPhone(employee.work_phone)}</dd>
                </>
              )}
              {employee.cell_phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Cell Phone</dt>
                  <dd>{formatPhone(employee.cell_phone)}</dd>
                </>
              )}
              {employee.home_phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Home Phone</dt>
                  <dd>{formatPhone(employee.home_phone)}</dd>
                </>
              )}
              {employee.report_display_name && (
                <>
                  <dt className="text-hpi-med-blue font-medium">
                    Name on Reports
                  </dt>
                  <dd>{employee.report_display_name}</dd>
                </>
              )}
              {employee.specialty && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Specialty</dt>
                  <dd className="whitespace-pre-wrap">{employee.specialty}</dd>
                </>
              )}
            </dl>
          </div>

          {/* Permissions */}
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Permissions</h2>
            {Object.entries(permission_categories).map(
              ([category, permissions]) => {
                const activePerms = permissions.filter(
                  (p) => employee.permissions[p],
                )
                if (activePerms.length === 0) return null
                return (
                  <div key={category} className="mb-3">
                    <h3 className="text-hpi-med-gray text-sm font-medium">
                      {category}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {activePerms.map((p) => (
                        <span
                          key={p}
                          className="bg-hpi-light-blue text-hpi-dark-blue rounded px-2 py-0.5 text-xs"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              },
            )}
            {Object.values(employee.permissions).filter(Boolean).length ===
              0 && (
              <p className="text-hpi-med-gray text-sm">No permissions set</p>
            )}
          </div>
        </div>

        {/* Pending Items */}
        {pending_items.length > 0 && (
          <div className="border-hpi-light-gray mt-6 rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">
              Pending{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                ({pending_items.length}{" "}
                {pending_items.length === 1 ? "item" : "items"})
              </span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-hpi-light-gray border-b text-left">
                    <th className="pr-4 pb-2 font-medium">Date</th>
                    <th className="pr-4 pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {pending_items.map((item) => {
                    const href = item.case_id
                      ? `/cases/${item.case_id}/${item.type === "Time" ? "time_entries" : item.type === "Expense" ? "expenses" : item.type === "Report" ? "reports" : "logs"}/${item.id}`
                      : item.customer_id
                        ? `/customers/${item.customer_id}/logs/${item.id}`
                        : undefined

                    return (
                      <tr
                        key={`${item.type}-${item.id}`}
                        className="border-hpi-light-gray border-b last:border-0"
                      >
                        <td className="py-2 pr-4 whitespace-nowrap">
                          {item.date && formatDate(item.date)}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`rounded px-2 py-0.5 text-xs font-medium ${
                              item.type === "Time"
                                ? "bg-blue-100 text-blue-800"
                                : item.type === "Expense"
                                  ? "bg-green-100 text-green-800"
                                  : item.type === "Report"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {item.type}
                          </span>
                        </td>
                        <td className="py-2">
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {item.case_name && (
                                <span className="text-hpi-med-gray">
                                  {item.case_name} —{" "}
                                </span>
                              )}
                              {item.description}
                            </a>
                          ) : (
                            <span>
                              {item.case_name && (
                                <span className="text-hpi-med-gray">
                                  {item.case_name} —{" "}
                                </span>
                              )}
                              {item.description}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Employee Logs */}
        <div className="border-hpi-light-gray mt-6 rounded border p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Employee Log</h2>
            <div className="flex items-center gap-2">
              <Link
                href={`${reportsEmployeeLogsPath()}?employee_id=${employee.id}`}
                className="text-hpi-active-blue text-sm hover:underline"
              >
                View All Employee Logs
              </Link>
              <button
                onClick={() => setShowAddLog(!showAddLog)}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-3 py-1 text-sm text-white"
              >
                {showAddLog ? "Cancel" : "Add Entry"}
              </button>
            </div>
          </div>

          <HpiDateFilterBar
            startDate={logStartDate}
            onStartDateChange={setLogStartDate}
            endDate={logEndDate}
            onEndDateChange={setLogEndDate}
            onUpdate={handleLogFilter}
            className="mb-4"
          />

          {showAddLog && (
            <form
              onSubmit={handleAddLog}
              className="bg-hpi-pale-gray mb-4 rounded p-4"
            >
              <div className="mb-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="log_date"
                    className="mb-1 block text-sm font-medium"
                  >
                    Date
                  </label>
                  <input
                    type="date"
                    id="log_date"
                    value={logForm.data.log_date}
                    onChange={(e) =>
                      logForm.setData("log_date", e.target.value)
                    }
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  />
                </div>
                {is_admin && (
                  <div className="flex items-end">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={logForm.data.hidden}
                        onChange={(e) =>
                          logForm.setData("hidden", e.target.checked)
                        }
                      />
                      <span className="text-sm">Hidden from employee</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="content"
                  className="mb-1 block text-sm font-medium"
                >
                  Content
                </label>
                <textarea
                  id="content"
                  value={logForm.data.content}
                  onChange={(e) => logForm.setData("content", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  rows={3}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={logForm.processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                Save Entry
              </button>
            </form>
          )}

          {logs.length > 0 ? (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`rounded border p-3 ${log.hidden ? "border-hpi-yellow bg-hpi-yellow/20" : "border-hpi-light-gray"}`}
                >
                  <div className="text-hpi-med-gray mb-1 flex items-center justify-between text-sm">
                    <span>
                      {log.log_date && formatDate(log.log_date)}
                      {log.created_by && ` - ${log.created_by}`}
                      {log.hidden && (
                        <span className="bg-hpi-yellow/30 text-hpi-dark-gray ml-2 rounded px-1.5 py-0.5 text-xs">
                          Hidden
                        </span>
                      )}
                    </span>
                    {is_admin && (
                      <button
                        onClick={() => handleDeleteLog(log.id)}
                        className="text-hpi-red hover:text-hpi-red/70 cursor-pointer"
                        title="Delete log entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{log.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-hpi-med-gray text-sm">No log entries.</p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
